import { apiCall } from './apiClient';

export type ResearchMethodStatus = 'planned' | 'in_progress' | 'performed' | 'inconclusive' | 'no_longer_relevant';
export type PlanningStatus = 'planned' | 'in_progress' | 'no_longer_relevant';
export interface EarlyResearchMethod {
  id: string;
  method_key: string;
  label: string;
  proposal_origin: 'generated_proposal';
  owner: string | null;
  role: string | null;
  due_date: string | null;
  acceptance_criterion: string | null;
  assignment_origin: 'unassigned' | 'user_confirmed';
  status: ResearchMethodStatus;
  evidence_references: string[];
  follow_up_required: boolean;
  expected_evidence?: string;
  history: unknown[];
}
export interface EarlyResearchPlan {
  schema_version: 1;
  requirements_digest: string;
  revision: number;
  can_edit: boolean;
  objectives: string[];
  unknowns: string[];
  assumptions: string[];
  methods: EarlyResearchMethod[];
  history: unknown[];
}
export interface ResearchMethodAssignment {
  id: string;
  owner?: string | null;
  role?: string | null;
  due_date?: string | null;
  acceptance_criterion?: string | null;
  status?: PlanningStatus;
}

const statuses = new Set(['planned', 'in_progress', 'performed', 'inconclusive', 'no_longer_relevant']);
const strings = (value: unknown): value is string[] => Array.isArray(value) && value.every(item => typeof item === 'string');
export function parseEarlyResearchPlan(value: unknown): EarlyResearchPlan | null {
  if (value === null) return null;
  const plan = value as EarlyResearchPlan;
  if (!plan || plan.schema_version !== 1 || typeof plan.requirements_digest !== 'string'
    || !Number.isInteger(plan.revision) || plan.revision < 0 || typeof plan.can_edit !== 'boolean'
    || !strings(plan.objectives) || !strings(plan.unknowns) || !strings(plan.assumptions)
    || !Array.isArray(plan.history) || !Array.isArray(plan.methods) || plan.methods.length > 30) throw new Error('Invalid research plan');
  const ids = new Set<string>();
  for (const method of plan.methods) {
    if (!method || typeof method.id !== 'string' || !method.id || ids.has(method.id)
      || typeof method.method_key !== 'string' || typeof method.label !== 'string'
      || method.proposal_origin !== 'generated_proposal' || !statuses.has(method.status)
      || !['unassigned', 'user_confirmed'].includes(method.assignment_origin)
      || !strings(method.evidence_references) || typeof method.follow_up_required !== 'boolean' || !Array.isArray(method.history)
      || (method.expected_evidence !== undefined && typeof method.expected_evidence !== 'string')
      || ['owner', 'role', 'due_date', 'acceptance_criterion'].some(key => method[key as keyof EarlyResearchMethod] !== null && typeof method[key as keyof EarlyResearchMethod] !== 'string')) throw new Error('Invalid research method');
    ids.add(method.id);
  }
  return plan;
}

export async function getEarlyResearchPlan(flowId: string): Promise<EarlyResearchPlan | null> {
  const response = await apiCall<unknown>(`/api/flows/${encodeURIComponent(flowId)}/research-plan`, 'GET', undefined, { retries: 0 });
  if (!response.success) throw new Error('Research plan could not be loaded');
  return parseEarlyResearchPlan(response.data);
}
export async function saveEarlyResearchPlan(flowId: string, expectedRevision: number, methods: ResearchMethodAssignment[]): Promise<EarlyResearchPlan> {
  const response = await apiCall<unknown>(`/api/flows/${encodeURIComponent(flowId)}/research-plan`, 'PATCH', { expected_revision: expectedRevision, methods }, { retries: 0 });
  if (!response.success) throw new Error('Research plan could not be saved');
  const plan = parseEarlyResearchPlan(response.data);
  if (!plan) throw new Error('Research plan could not be saved');
  return plan;
}
